import {
  getAuthApiUrl,
  getStoredSessionToken,
  isSessionExpiredPayload,
  isSessionRefreshRequiredPayload,
} from '@/app/services/session.service';

const ADMIN_ASSETS_API_PATH = '/api/admin/assets';

function buildResult({ ok, data = null, error = null, message = '' }) {
  return { ok, data, error, message };
}

function getAuthHeaders(extraHeaders = {}) {
  const token = getStoredSessionToken();
  return token ? { ...extraHeaders, Authorization: `Bearer ${token}` } : extraHeaders;
}

async function parseJson(response) {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

function mapAsset(asset) {
  return {
    id: asset?.id || '',
    scope: asset?.scope || '',
    tenantId: asset?.tenantId || '',
    usageKey: asset?.usageKey || '',
    displayName: asset?.displayName || '',
    originalFilename: asset?.originalFilename || '',
    mimeType: asset?.mimeType || '',
    fileSizeBytes: asset?.fileSizeBytes || 0,
    status: asset?.status || '',
    publicUrl: asset?.publicUrl || '',
    createdAt: asset?.createdAt || null,
    updatedAt: asset?.updatedAt || null,
  };
}

async function mapAssetError(response, fallbackError, fallbackMessage, payload = undefined) {
  const responsePayload = payload === undefined ? await parseJson(response) : payload;
  const message = responsePayload?.message || fallbackMessage;

  if (response.status === 401 && isSessionRefreshRequiredPayload(responsePayload)) {
    return buildResult({ ok: false, error: 'SESSION_REFRESH_REQUIRED', message });
  }
  if (response.status === 401 && isSessionExpiredPayload(responsePayload)) {
    return buildResult({ ok: false, error: 'SESSION_EXPIRED', message });
  }
  if (response.status === 403) {
    return buildResult({ ok: false, error: 'ADMIN_FORBIDDEN', message: message || 'Accesso admin non consentito.' });
  }
  if (response.status === 400) {
    return buildResult({ ok: false, error: 'ASSET_VALIDATION_FAILED', message: message || 'Asset non valido.' });
  }
  if (response.status === 404) {
    return buildResult({ ok: false, error: 'ASSET_UNAVAILABLE', message: message || 'Asset non disponibile.' });
  }
  return buildResult({ ok: false, error: fallbackError, message });
}

export async function listAdminAssets({ scope, tenantId, usageKey } = {}) {
  try {
    const params = new URLSearchParams();
    if (scope) params.set('scope', scope);
    if (tenantId) params.set('tenantId', tenantId);
    if (usageKey) params.set('usageKey', usageKey);
    const query = params.toString();
    const response = await fetch(getAuthApiUrl(`${ADMIN_ASSETS_API_PATH}${query ? `?${query}` : ''}`), {
      headers: getAuthHeaders(),
    });
    const payload = await parseJson(response);
    if (!response.ok) {
      return mapAssetError(response, 'ASSET_UNAVAILABLE', 'Asset non disponibili.', payload);
    }
    return buildResult({ ok: true, data: { assets: Array.isArray(payload) ? payload.map(mapAsset) : [] } });
  } catch {
    return buildResult({ ok: false, error: 'ASSET_UNAVAILABLE', message: 'Backend asset non disponibile.' });
  }
}

export async function uploadAdminAsset({ file, scope, tenantId, usageKey, displayName, autoAssign = false }) {
  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('metadata', JSON.stringify({ scope, tenantId: tenantId || null, usageKey, displayName, autoAssign }));
    const response = await fetch(getAuthApiUrl(ADMIN_ASSETS_API_PATH), {
      method: 'POST',
      headers: getAuthHeaders(),
      body: formData,
    });
    const payload = await parseJson(response);
    if (!response.ok) {
      return mapAssetError(response, 'ASSET_UPLOAD_FAILED', 'Upload asset non completato.', payload);
    }
    return buildResult({ ok: true, data: { asset: mapAsset(payload) }, message: 'Asset caricato.' });
  } catch {
    return buildResult({ ok: false, error: 'ASSET_UPLOAD_FAILED', message: 'Upload asset non completato.' });
  }
}

export async function assignAdminAsset({ assetId, tenantId, assignmentKey }) {
  try {
    const response = await fetch(getAuthApiUrl(`${ADMIN_ASSETS_API_PATH}/${assetId}/assign`), {
      method: 'POST',
      headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ tenantId: tenantId || null, assignmentKey }),
    });
    const payload = await parseJson(response);
    if (!response.ok) {
      return mapAssetError(response, 'ASSET_UPLOAD_FAILED', 'Assegnazione asset non completata.', payload);
    }
    return buildResult({ ok: true, data: { asset: mapAsset(payload) }, message: 'Asset assegnato.' });
  } catch {
    return buildResult({ ok: false, error: 'ASSET_UPLOAD_FAILED', message: 'Assegnazione asset non completata.' });
  }
}

export function getAdminAssetDownloadUrl(assetId) {
  return getAuthApiUrl(`${ADMIN_ASSETS_API_PATH}/${assetId}/download`);
}

export function getPublicAssetUrl(assetId) {
  return getAuthApiUrl(`/api/assets/public/${assetId}`);
}
